import {apiRequest} from './client';

export interface Theme {
    visibility?: 'public' | 'private';
    id: string;
    name: string;
    slug: string;
    description: string;
    tagline: string | null;
    thumbnail_url: string | null;
    preview_url: string | null;
    price_cents: number | null;
    state: 'free' | 'owned' | 'premium';
    is_free: boolean;
    included_with_all_access: boolean;
    product_id: string | null;
    entitlement_state: string;
    entitlement_reason: string;
    installed_version: string | null;
    available_version: string | null;
    update_available: boolean;
}

export interface BundleInfo {
    status: string;
    term: string | null;
    expires_at: string | null;
}

export interface BundleProduct {
    id: string;
    term: string;
}

export interface MarketplaceThemesResponse {
    themes: Theme[];
    bundle: BundleInfo | null;
    bundleProducts: BundleProduct[];
    billing_portal_url: string | null;
}

export interface CheckoutRequest {
    productId: string;
}

export interface CheckoutResponse {
    // Matches marketplace-checkout.service createCheckout(): {checkoutUrl, nonce}
    checkoutUrl: string;
    nonce: string;
}

export interface MarketplaceAdminVersion {
    id: string;
    version: string;
    artifact_hash: string;
    gscan_result: {warnings?: unknown[]; errors?: unknown[]} | null;
    custom_settings_metadata: unknown;
    created_at: string;
}

export interface MarketplaceAdminTheme {
    id: string;
    slug: string;
    name: string;
    description: string | null;
    author: string | null;
    license_spdx: string | null;
    thumbnail_url: string | null;
    demo_url: string | null;
    status: 'draft' | 'published' | 'archived';
    is_free: boolean;
    price_cents: number | null;
    current_version: string | null;
    current_artifact_hash: string | null;
    gscan_passed: boolean;
    versions?: MarketplaceAdminVersion[];
}

export type MarketplaceAdminCustomTheme = Pick<MarketplaceAdminTheme,
    'id' | 'slug' | 'name' | 'description' | 'thumbnail_url' | 'current_version' | 'gscan_passed'
>;

export async function getMarketplaceThemes(): Promise<MarketplaceThemesResponse> {
    return apiRequest<MarketplaceThemesResponse>('/marketplace/themes', {
        method: 'GET'
    });
}

export async function createCheckout(data: CheckoutRequest): Promise<CheckoutResponse> {
    return apiRequest<CheckoutResponse>('/marketplace/checkout', {
        method: 'POST',
        body: JSON.stringify(data)
    });
}

export async function getAdminMarketplaceThemes(): Promise<MarketplaceAdminTheme[]> {
    return apiRequest('/admin/marketplace', {method: 'GET'});
}

export async function getAdminMarketplaceCustomThemes(): Promise<MarketplaceAdminCustomTheme[]> {
    return apiRequest('/admin/marketplace/custom-themes', {method: 'GET'});
}

export async function getAdminMarketplaceTheme(id: string): Promise<MarketplaceAdminTheme> {
    return apiRequest(`/admin/marketplace/${id}`, {method: 'GET'});
}

export async function getAdminMarketplaceSlugAvailability(slug: string): Promise<{slug: string; available: boolean}> {
    return apiRequest(`/admin/marketplace/slug-availability/${encodeURIComponent(slug)}`, {method: 'GET'});
}

export async function createAdminMarketplaceTheme(data: Pick<MarketplaceAdminTheme, 'slug' | 'name'> & Partial<MarketplaceAdminTheme>): Promise<MarketplaceAdminTheme> {
    return apiRequest('/admin/marketplace', {method: 'POST', body: JSON.stringify(data)});
}

export async function importAdminMarketplaceCustomTheme(id: string, data: Pick<MarketplaceAdminTheme, 'slug' | 'name'> & {description: string}): Promise<MarketplaceAdminTheme> {
    return apiRequest(`/admin/marketplace/custom-themes/${id}/import`, {method: 'POST', body: JSON.stringify(data)});
}

export async function deleteAdminMarketplaceTheme(id: string): Promise<{id: string}> {
    return apiRequest(`/admin/marketplace/${id}`, {method: 'DELETE'});
}

export async function uploadAdminMarketplaceVersion(id: string, file: File): Promise<{theme: MarketplaceAdminTheme; version: MarketplaceAdminVersion}> {
    const body = new FormData();
    body.append('file', file);
    return apiRequest(`/admin/marketplace/${id}/versions`, {method: 'POST', body});
}

export async function setAdminMarketplacePricing(id: string, data: {is_free: boolean; display_price_cents: number | null; paykickstart_product_id: string | null}): Promise<MarketplaceAdminTheme> {
    return apiRequest(`/admin/marketplace/${id}/pricing`, {method: 'PATCH', body: JSON.stringify(data)});
}

export async function setAdminMarketplacePublished(id: string, published: boolean): Promise<MarketplaceAdminTheme> {
    return apiRequest(`/admin/marketplace/${id}/${published ? 'publish' : 'unpublish'}`, {method: 'POST'});
}
